import React, { useState, useEffect, useMemo, useRef } from "react"
import { sbEditable } from "@storyblok/storyblok-editable"
import { useStoryblok } from "../utils/storyblok"
import DynamicComponents from "../components/DynamicComponents"

import CollectionHero from "../components/collectionHero/collectionHero"
import ProductCard from "../components/ProductCard/index.js"
import Spinner from "../components/Icons/Progress/index.js"
import "./collections.scss"
import { progressStyle } from "../pages/searchPage.module.css"
import CollectionPagination from "../components/CollectionPagination"
import Seo from "../components/seo"

const DEFAULT_PRODUCTS_PER_PAGE = 24

export const Collection = ({ pageContext }) => {
  const { collection, product, storyblokEntry } = pageContext

  const story = storyblokEntry
    ? useStoryblok(storyblokEntry.node)
    : { content: null }
  const queryParams = {
    maxPrice: undefined,
    minPrice: undefined,
    productTypes: [],
    sortKey: undefined,
    tags: [],
    term: undefined,
    vendors: [],
  }
  const [isLoading, setIsLoading] = useState(false)
  const [filters, setFilters] = useState(queryParams)
  const [showModal, setShowModal] = useState(false)
  const [filterTagLibraryState, setFilterTagLibraryState] = useState({})
  const [tagArrayState, setTagArrayState] = useState([])

  const [currentPageAllProducts, setCurrentPageAllProducts] = useState(
    collection.products,
  )
  const [currentPageProducts, setCurrentPageProducts] = useState([])
  const [currentPageNumber, setCurrentPageNumber] = useState(1)

  const initialCollectionPagination = Math.ceil(
    collection.products.length / DEFAULT_PRODUCTS_PER_PAGE,
  )
  const [totalCollectionPageNumber, setTotalCollectionPageNumber] = useState(
    initialCollectionPagination,
  )
  const [paginationArray, setPaginationArray] = useState([])

  useEffect(() => {
    if (!showModal) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      })
    }
  }, [currentPageNumber, showModal])

  const createPaginationArray = totalPages => {
    const paginationArray = []
    for (let i = 0; i < totalPages; i += 1) {
      paginationArray.push(i + 1)
    }

    setPaginationArray(paginationArray)
  }
  useEffect(() => {
    createPaginationArray(totalCollectionPageNumber)
  }, [totalCollectionPageNumber])

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    })
  }

  useEffect(() => {
    // TODO: Set a state that tracks current products length
    const PRODUCT_LISTINGS_ON_PAGE =
      DEFAULT_PRODUCTS_PER_PAGE * currentPageNumber
    const totalProducts = currentPageAllProducts.length
    setCurrentPageProducts(
      PRODUCT_LISTINGS_ON_PAGE > totalProducts
        ? currentPageAllProducts.slice(
            PRODUCT_LISTINGS_ON_PAGE - 24,
            totalProducts,
          )
        : currentPageAllProducts.slice(
            PRODUCT_LISTINGS_ON_PAGE - 24,
            PRODUCT_LISTINGS_ON_PAGE,
          ),
    )
    scrollToTop()
  }, [currentPageNumber, currentPageAllProducts])

  /*
   * Use filteredProducts to load in products that are filtered by tags
   * Update currentPageProducts state with the filteredProducts when filtration is done.
   * use checkFilters for setting previous page and next page
   */

  const tagSet = new Set()
  const tagTitleSet = new Set()
  const filterTagLibrary = {}

  useEffect(() => {
    const totalProducts = collection.products.length

    collection.products.forEach(product => {
      const productTagsCollection = new Set()

      product.tags.forEach(tag => {
        if (tag.includes(":")) {
          tagSet.add(tag)
          productTagsCollection.add(tag)
        }
      })

      product.clientTags = productTagsCollection
    })

    tagSet.forEach(tag => {
      const colonPosition = tag.indexOf(":")
      const productType = tag.slice(colonPosition + 1)

      tagTitleSet.add(tag.slice(0, colonPosition))

      if (filterTagLibrary[tag.slice(0, colonPosition)]) {
        filterTagLibrary[tag.slice(0, colonPosition)].push(productType)
      } else {
        filterTagLibrary[tag.slice(0, colonPosition)] = [productType]
      }
    })

    setCurrentPageProducts(
      collection.products.slice(
        0,
        DEFAULT_PRODUCTS_PER_PAGE < totalProducts
          ? DEFAULT_PRODUCTS_PER_PAGE
          : totalProducts,
      ),
    )

    const tagArray = Array.from(tagTitleSet)

    setTagArrayState(tagArray)
    setFilterTagLibraryState({ ...filterTagLibrary })
  }, [])

  useEffect(() => {
    if (filters.tags.length === 0) {
      setCurrentPageAllProducts(collection.products)
    } else {
      const filteredProducts = []
      const selectedTagsSet = new Set(filters.tags)
      for (const product of collection.products) {
        for (const element of selectedTagsSet) {
          if (product.clientTags.has(element)) {
            filteredProducts.push(product)
            break
          }
        }
      }
      setCurrentPageAllProducts(filteredProducts)
    }
  }, [filters])

  useEffect(() => {
    setTotalCollectionPageNumber(
      Math.ceil(currentPageAllProducts.length / DEFAULT_PRODUCTS_PER_PAGE),
    )
  }, [currentPageAllProducts])

  const stickyNavComponent =
    story.content && story.content.sticky_nav
      ? story.content.sticky_nav.map(blok => (
          <>
            {blok ? (
              <div {...sbEditable(blok)} className="sticky top-0 z-50">
                <DynamicComponents
                  blok={blok}
                  product={product}
                  key={blok._uid}
                />
              </div>
            ) : null}
          </>
        ))
      : null
  // Load Yotpo Reviews Script
  useEffect(() => {
    const script = document.createElement("script")
    script.src =
      "https://staticw2.yotpo.com/qNmEg7HN0QiYfqn6n5DHRUGCtTUGNWa1GfSVsHRS/widget.js"
    script.async = true
    document.head.appendChild(script)

    script.onload = e => {
      window.yotpo.refreshWidgets()
    }

    return () => {
      script.remove()
    }
  })

  return (
    <>
      <Seo title={collection.title} description={collection.description} />
      <div className="collections-wrapper">
        {stickyNavComponent}
        <CollectionHero image={collection.image} title={collection.title} />
        <div className="w-full mx-auto px-[15px] md:px-[6vw] xl:px-[7rem] max-w-max 3xl:max-w-[90%]">
          <div className="flex flex-wrap w-full mx-auto collections-below-wrapper px-1 pt-10 pb-10 md:px-0 mb-0">
            <div className={`w-full ${showModal ? "hidden" : "grid"} md:grid`}>
              <div className="flex items-center justify-between"></div>
              <div className="grid collectionsGrid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-x-5 md:gap-x-10 gap-y-5">
                {isLoading ? (
                  <p className={progressStyle}>
                    <Spinner aria-valuetext="Searching" /> Loading.=
                    {filters.term ? ` for "${filters.term}"…` : `…`}
                  </p>
                ) : (
                  currentPageProducts.map(product => (
                    <ProductCard product={product} key={product.handle} />
                  ))
                )}
              </div>
              {totalCollectionPageNumber > 1 ? (
                <CollectionPagination
                  paginationArray={paginationArray}
                  totalCollectionPageNumber={totalCollectionPageNumber}
                  currentPageNumber={currentPageNumber}
                  setCurrentPageNumber={setCurrentPageNumber}
                />
              ) : (
                ""
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Collection
